import React, { useMemo, useState } from 'react';
import { getLivePrice, getTradeLock, getRowImage } from './helpers';
import { isFlipRow } from './investments';

// Live 3 — "Blotter": holdings as a dense pro trading table with sortable
// columns and a diverging P/L bar. Clicking a row opens the standard editor.
const COND_ABBR = {
  'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field Tested': 'FT',
  'Well Worn': 'WW', 'Battle Scarred': 'BS',
};

function getItemTypes(row) {
  if (!row?.itemType) return [];
  return Array.isArray(row.itemType) ? row.itemType : [row.itemType];
}

const COLS = [
  { key: 'paid',   label: 'Paid' },
  { key: 'market', label: 'Market' },
  { key: 'pl',     label: 'P/L $' },
  { key: 'pct',    label: 'P/L %' },
];

export default function LiveTab3({ rows, settings, livePrices, skinsData = [], inventoryMap, onEdit }) {
  const [query, setQuery]     = useState('');
  const [sortKey, setSortKey] = useState('paid');
  const [desc, setDesc]       = useState(true);

  const holdings = useMemo(() => {
    return (rows || [])
      .map((row, realIndex) => ({ row, realIndex }))
      // Investments live in the Vault only — one item, one home.
      .filter(({ row }) => isFlipRow(row))
      .map(e => {
        const paid   = parseFloat(e.row.buyPrice) || 0;
        const lp     = getLivePrice(livePrices, e.row);
        const market = lp?.starting_at?.price ?? null;
        const pl     = market != null ? market - paid : null;
        const pct    = pl != null && paid > 0 ? pl / paid * 100 : null;
        const inv    = e.row.assetId ? inventoryMap?.get(e.row.assetId) : null;
        const locked = getTradeLock(e.row, inv).locked;
        const type   = getItemTypes(e.row)
          .map(t => settings?.itemTypes?.find(it => it.value === t.value))
          .find(Boolean);
        const days   = Math.max(0, Math.floor((Date.now() - new Date(e.row.timestamp)) / 86400000));
        return { ...e, paid, market, pl, pct, locked, type, days };
      });
  }, [rows, livePrices, settings, inventoryMap]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q ? holdings.filter(h => h.row.skin.value.toLowerCase().includes(q)) : [...holdings];
    const dir = desc ? -1 : 1;
    const val = (h) => {
      switch (sortKey) {
        case 'paid':   return h.paid;
        case 'market': return h.market ?? -Infinity;
        case 'pl':     return h.pl ?? -Infinity;
        case 'pct':    return h.pct ?? -Infinity;
        case 'age':    return h.days;
        default:       return h.paid;
      }
    };
    return list.sort((a, b) => (val(a) - val(b)) * dir);
  }, [holdings, query, sortKey, desc]);

  const maxPct = useMemo(
    () => Math.max(1, ...filtered.map(h => Math.abs(h.pct ?? 0))),
    [filtered],
  );

  const totals = useMemo(() => {
    const paid = filtered.reduce((s, h) => s + h.paid, 0);
    let market = 0, pl = 0, known = false;
    filtered.forEach(h => {
      if (h.market != null) { market += h.market; pl += h.pl; known = true; }
      else market += h.paid;
    });
    return { paid, market, pl: known ? pl : null };
  }, [filtered]);

  const onSort = (key) => {
    if (sortKey === key) setDesc(d => !d);
    else { setSortKey(key); setDesc(true); }
  };

  const profC = settings?.profitTextColor || 'var(--green)';
  const lossC = settings?.lossTextColor   || 'var(--red)';

  return (
    <div className="lv3-root">

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar" style={{ position: 'static', marginBottom: 14 }}>
        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Filter blotter…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <span style={{ fontSize: '0.92rem', color: 'var(--text-dim)', fontWeight: 600 }}>
          {filtered.length} open position{filtered.length !== 1 ? 's' : ''}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '0.92rem', fontWeight: 700, color: totals.pl == null ? 'var(--text-dim)' : totals.pl >= 0 ? profC : lossC }}>
          {totals.pl == null ? 'Awaiting prices' : `${totals.pl >= 0 ? '+' : '−'}$${Math.abs(totals.pl).toFixed(2)} unrealized`}
        </span>
      </div>

      {/* ── Blotter ────────────────────────────────── */}
      <div className="lv3-panel">
        <div className="lv3-scroll">
          <div className="lv3-table">
            <div className="lv3-thead">
              <button className="lv3-th left static" tabIndex={-1}>Item</button>
              {COLS.map(c => (
                <button key={c.key} className="lv3-th" onClick={() => onSort(c.key)}>
                  {c.label}
                  <span className="arr">{sortKey === c.key ? (desc ? '▼' : '▲') : '↕'}</span>
                </button>
              ))}
              <button className="lv3-th static" tabIndex={-1}>P/L range</button>
              <button className="lv3-th" onClick={() => onSort('age')}>
                Age<span className="arr">{sortKey === 'age' ? (desc ? '▼' : '▲') : '↕'}</span>
              </button>
            </div>

            {filtered.length === 0 && (
              <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.98rem' }}>
                {holdings.length === 0 ? 'No open positions.' : 'Nothing matches the filter.'}
              </div>
            )}

            {filtered.map(h => {
              const img = getRowImage(h.row, skinsData, inventoryMap);
              const barPct = h.pct != null ? Math.min(50, Math.abs(h.pct) / maxPct * 50) : 0;
              return (
                <div key={h.realIndex} className="lv3-trow" onClick={() => onEdit && onEdit(h.row, h.realIndex)}>
                  <div className="lv3-item">
                    {img && <img src={img} alt="" />}
                    <div style={{ minWidth: 0 }}>
                      <div className="nm" title={h.row.skin.value}>{h.row.skin.value}</div>
                      <div className="mt">
                        <span>{COND_ABBR[h.row.condition?.value] || h.row.condition?.value || '—'}</span>
                        {h.type && <span style={{ color: h.type.color }}>{h.type.label}</span>}
                        {h.locked && <><span className="lv3-lockdot" /><span style={{ color: 'var(--amber)' }}>locked</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="lv3-num strong">${h.paid.toFixed(2)}</div>
                  <div className="lv3-num">{h.market != null ? `$${h.market.toFixed(2)}` : '—'}</div>
                  <div className="lv3-num strong" style={{ color: h.pl == null ? 'var(--text-dim)' : h.pl >= 0 ? profC : lossC }}>
                    {h.pl == null ? '—' : `${h.pl >= 0 ? '+' : '−'}$${Math.abs(h.pl).toFixed(2)}`}
                  </div>
                  <div className="lv3-num" style={{ color: h.pct == null ? 'var(--text-dim)' : h.pct >= 0 ? profC : lossC }}>
                    {h.pct == null ? '—' : `${h.pct >= 0 ? '+' : '−'}${Math.abs(h.pct).toFixed(1)}%`}
                  </div>
                  <div className="lv3-divbar" title={h.pct != null ? `${h.pct.toFixed(1)}% vs paid` : 'No live price'}>
                    <span className="axis" />
                    {h.pct != null && (
                      <span className={`fill ${h.pct >= 0 ? 'up' : 'down'}`} style={{ width: `${barPct}%` }} />
                    )}
                  </div>
                  <div className="lv3-num">{h.days}d</div>
                </div>
              );
            })}

            {filtered.length > 0 && (
              <div className="lv3-tfoot">
                <div className="lv3-num strong" style={{ textAlign: 'left', fontFamily: 'var(--font-body)', fontSize: '0.91rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-dim)' }}>
                  Totals
                </div>
                <div className="lv3-num strong">${totals.paid.toFixed(2)}</div>
                <div className="lv3-num strong">${totals.market.toFixed(2)}</div>
                <div className="lv3-num strong" style={{ color: totals.pl == null ? 'var(--text-dim)' : totals.pl >= 0 ? profC : lossC }}>
                  {totals.pl == null ? '—' : `${totals.pl >= 0 ? '+' : '−'}$${Math.abs(totals.pl).toFixed(2)}`}
                </div>
                <div className="lv3-num" style={{ color: 'var(--text-dim)' }}>
                  {totals.paid > 0 && totals.pl != null ? `${totals.pl >= 0 ? '+' : '−'}${Math.abs(totals.pl / totals.paid * 100).toFixed(1)}%` : ''}
                </div>
                <div />
                <div />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
